import React, { useMemo, useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Box, FormControl, NativeSelect, FormHelperText, Grid, TextField, Button } from "@material-ui/core";

import { useForm } from "../../../hooks/useForm";
import { trowError, trowErrorMessage } from "../../../utils/errors";
import { tableSelector, formIsError } from "../../../redux/tableReducer";
import { hallSelector } from "../../../redux/hallReducer";
import { authSelector } from "../../../redux/authReducer";
import { findPermission } from "../../../utils/find";

import Preloader from "../../Common/Preloader";
import SwitchStatus from "../../Common/SwitchStatus";
import ErrorText from "../../Common/ErrorText";
import JackpotsList from "../../Jackpots/List";
import Denominations from "./denominations";
import Api from "./api";

import { IPackage, IGame, TableData, GamesType, ITableApi } from "../../../interfaces";
import { TabItem } from "../../Halls/View/styles";
import { useStyles } from "./styles";
import { TAB_API, TAB_DENOMINATION, TAB_JACKPOTS, TAB_MAIN, TAB_PACKETS } from "../tabs";
import Packets from "../../Halls/Form/tabs/packets";

interface TableFormProps {
    onSubmit: (data: TableData, packages: any, hideForm: () => void, id?: number, info?: boolean) => void;
    name?: string;
    id?: number | undefined;
    hall_id?: number;
    packets?: IPackage[];
    status?: boolean;
    cancelBtnText: string;
    cancelBtnEvent: () => void;
    submitBtnText: string;
    hideForm: () => void;
    defaultHall?: number;
    setJackpotId?: (id: number) => void;
    showJackpot?: () => void;
    showAddJackpot?: () => void;
    showChangeJackpot?: () => void;
    tab: number;
    setTab: (id: number) => void;
    denomination?: number;
    denominationList?: number[] | null;
    denom?: string;
    api?: ITableApi;
    phone?: string | null;
    email?: string | null;
}

const TableForm: React.FC<TableFormProps> = ({
    onSubmit, name = "", id, hall_id = "", packets = [], status = false,
    cancelBtnText, cancelBtnEvent, submitBtnText, hideForm, defaultHall,
    tab, setTab, denomination, denominationList, api, phone = "", email = "",
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [t] = useTranslation();

    const { form: { error, loading: formLoading }, tables } = useSelector(tableSelector);
    const { halls: { data: halls } } = useSelector(hallSelector);
    const { role: { item: userRole }, assigments: { items: assigments } } = useSelector(authSelector);

    const [isInherited, setIsInherited] = useState<boolean>(!packets || packets.length === 0);

    const getGamesId = useCallback((games: IGame[]) => {
        return games.map((item) => item.id);
    }, []);

    const [packagesSelected, setPackagesSelected] = useState<number[]>(() => {
        if (!packets) return [];
        return packets.map(item => item.package.id);
    });

    const [gamesSelected, setGamesSelected] = useState<GamesType[]>(() => {
        if (!packets) return [];
        return packets.map(item => {
            const gamesIds = getGamesId(item.games);
            return {
                package_id: item.package.id,
                mode: gamesIds.length === 0 ? "ALL_PROVIDER" : "CUSTOM",
                games: gamesIds,
            };
        });
    });

    const canViewGamePackages = useMemo(() => findPermission(assigments, "viewGamePackageList"), [assigments]);
    const canShowApi = useMemo(() => findPermission(assigments, "showApi"), [assigments]);

    const getPackagesData = useCallback(() => {
        // Если включено глобальное наследование (isInherited === true)
        if (isInherited) {
            return { packages: [] };
        }

        // Если ручная настройка пакетов
        const data = packagesSelected.map((packageId: number) => {
            const selection = gamesSelected.find((i: GamesType) => i.package_id === packageId);

            return {
                package: { id: packageId },
                // Если ALL_PROVIDER, принудительно отправляем пустой массив
                games: selection?.mode === "ALL_PROVIDER" ? [] : (selection?.games || []),
            };
        });

        return { packages: data };
    }, [isInherited, packagesSelected, gamesSelected]);

    const { form, handleInputChange, setFormItem } = useForm({
        name,
        hall_id: defaultHall ? defaultHall : hall_id,
        password: "",
        status,
        denomination,
        denominationList,
        denom: "",
        remote_url: api?.remote_url ?? "",
        phone: phone ?? "",
        email: email ?? "",
    });

    const data: TableData = useMemo(() => ({
        name: form.name,
        hall_id: form.hall_id,
        login: "",
        password: form.password,
        status: form.status ? 10 : 0,
        denomination: form.denomination,
        denominations: form.denominationList,
        remote_url: form.remote_url,
        phone: form.phone,
        email: form.email,
    }), [form]);

    // Очистка ошибок при размонтировании
    useEffect(() => {
        return () => { dispatch(formIsError(null)); };
    }, [dispatch]);

    // Глобальный обработчик только для Escape. Enter обрабатывается нативно формой.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.code === "Escape") hideForm();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [hideForm]);

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(data, getPackagesData(), hideForm, id);
    };

    const hall = useMemo(() => halls.find(v => v.id === hall_id), [halls, hall_id]);
    const table = useMemo(() => tables.data.find(v => v.id === id), [tables.data, id]);

    const renderMainTab = () => (
        <React.Fragment>
            <Grid item xs={12} sm={7}>
                <TextField className={classes.textFields} label={t("ru.tables.fields.name")} type="text" value={form.name} name="name" onChange={handleInputChange} error={!!trowError(error, "name")} helperText={trowErrorMessage(error, "name")} />
            </Grid>
            <Grid item xs={12} sm={2}>
                <FormControl error={!!trowError(error, "hall_id")}>
                    <NativeSelect className={classes.select} value={form.hall_id} onChange={(e) => setFormItem("hall_id", e.target.value)} disabled={!!defaultHall}>
                        <option value=""></option>
                        {halls?.map((item: any) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </NativeSelect>
                    <FormHelperText>{trowErrorMessage(error, "hall_id", t("ru.tables.form_helpers.hall"))}</FormHelperText>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
                <Box className={classes.textFields} display="flex" justifyContent="flex-start" alignItems="center" onClick={() => setFormItem("status", !form.status)} style={{ cursor: "pointer" }}>
                    <SwitchStatus checked={!!form.status} />
                </Box>
            </Grid>
            {id && (
                <Grid item xs={12} sm={12}>
                    <TextField className={classes.textFields} label={t("ru.tables.fields.password")} type="text" value={form.password} name="password" onChange={handleInputChange} error={!!trowError(error, "password")} helperText={trowErrorMessage(error, "password")} />
                </Grid>
            )}
            <Grid item xs={12} sm={6}>
                <TextField className={classes.textFields} label={t("ru.tables.fields.phone")} type="text" value={form.phone} name="phone" onChange={handleInputChange} error={!!trowError(error, "phone")} helperText={trowErrorMessage(error, "phone")} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField className={classes.textFields} label={t("ru.tables.fields.email")} type="text" value={form.email} name="email" onChange={handleInputChange} error={!!trowError(error, "email")} helperText={trowErrorMessage(error, "email")} />
            </Grid>
        </React.Fragment>
    );

    const returnTab = (tab_id: number) => {
        switch (tab_id) {
            case TAB_MAIN:
                return renderMainTab();
            case TAB_PACKETS:
                return (
                    <Packets
                        isInherited={isInherited}
                        setIsInherited={setIsInherited}
                        packagesSelected={packagesSelected}
                        setPackagesSelected={setPackagesSelected}
                        gamesSelected={gamesSelected}
                        setGamesSelected={setGamesSelected}
                        getGamesId={getGamesId}
                    />
                );
            case TAB_JACKPOTS:
                return <JackpotsList table_id={id} />;
            case TAB_DENOMINATION:
                return (
                    <Denominations
                        setDenom={(x: string) => setFormItem("denom", x)}
                        setDenominations={(x: number[] | null) => setFormItem("denominationList", x)}
                        form={form}
                        error={error}
                        hallDenominations={(hall?.denominations || []).map(e => +e)}
                        setDefaultDenomination={(x: number) => setFormItem("denomination", x)}
                    />
                );
            case TAB_API:
                return table?.api ? <Api form={form} error={error} setFormItem={setFormItem} /> : null;
            default:
                setTab(TAB_MAIN);
                return null;
        }
    };

    if (formLoading) return <Preloader absolute={false} />;

    return (
        <form onSubmit={handleFormSubmit}>
            <Box className="tabs" display="flex" justifyContent="flex-start" style={{ overflowX: "auto", marginBottom: "15px" }}>
                <TabItem active={tab === TAB_MAIN} onClick={() => setTab(TAB_MAIN)}>
                    {t("ru.tables.tabs.info")}
                </TabItem>
                <TabItem active={tab === TAB_DENOMINATION} onClick={() => setTab(TAB_DENOMINATION)}>
                    {t("ru.tables.tabs.denomination")}
                </TabItem>
                {canViewGamePackages && (
                    <TabItem active={tab === TAB_PACKETS} onClick={() => setTab(TAB_PACKETS)}>
                        {t("ru.tables.tabs.packets")}
                    </TabItem>
                )}
                {id && userRole && userRole !== "cashier" && (
                    <TabItem active={tab === TAB_JACKPOTS} onClick={() => setTab(TAB_JACKPOTS)}>
                        {t("ru.tables.tabs.jackpots")}
                    </TabItem>
                )}
                {id && canShowApi && table?.api && (
                    <TabItem active={tab === TAB_API} onClick={() => setTab(TAB_API)}>
                        {t("ru.tables.tabs.api")}
                    </TabItem>
                )}
            </Box>

            <Grid container spacing={3}>
                {returnTab(tab)}
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <Button fullWidth className={classes.submitBtn} variant="contained" onClick={cancelBtnEvent}>
                            {cancelBtnText}
                        </Button>
                        <Button fullWidth className={classes.submitBtn} variant="contained" type="submit">
                            {submitBtnText}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <ErrorText error={error} />
                </Grid>
            </Grid>
        </form>
    );
};

export default TableForm;